import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ChevronDown, Settings, Check, Cookie } from 'lucide-react';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';

const ease = [0.16, 1, 0.3, 1];

function useCookieCategories() {
  const { t } = useLanguage();
  return useMemo(() => ([
    {
      id: 'necessary',
      name: t('cookie.cat.necessary.name'),
      description: t('cookie.cat.necessary.desc'),
      required: true,
      cookies: [
        { name: 'access_token', purpose: t('cookie.c.accessToken'), duration: t('cookie.dur.1h') },
        { name: 'refresh_token', purpose: t('cookie.c.refreshToken'), duration: t('cookie.dur.7d') },
        { name: 'cookie_consent', purpose: t('cookie.c.consent'), duration: t('cookie.dur.12m') },
        { name: 'zertivo_lang', purpose: t('cookie.c.lang'), duration: t('cookie.dur.12m') },
      ],
    },
    {
      id: 'analytics',
      name: t('cookie.cat.analytics.name'),
      description: t('cookie.cat.analytics.desc'),
      required: false,
      cookies: [
        { name: t('cookie.c.usage'), purpose: t('cookie.c.usageP'), duration: t('cookie.dur.12m') },
        { name: t('cookie.c.perf'), purpose: t('cookie.c.perfP'), duration: t('cookie.dur.6m') },
      ],
    },
    {
      id: 'marketing',
      name: t('cookie.cat.marketing.name'),
      description: t('cookie.cat.marketing.desc'),
      required: false,
      cookies: [
        { name: t('cookie.c.campaign'), purpose: t('cookie.c.campaignP'), duration: t('cookie.dur.6m') },
        { name: t('cookie.c.remarket'), purpose: t('cookie.c.remarketP'), duration: t('cookie.dur.12m') },
      ],
    },
    {
      id: 'preferences',
      name: t('cookie.cat.preferences.name'),
      description: t('cookie.cat.preferences.desc'),
      required: false,
      cookies: [
        { name: 'zertivo_lang', purpose: t('cookie.c.lang'), duration: t('cookie.dur.12m') },
        { name: t('cookie.c.prefs'), purpose: t('cookie.c.prefsP'), duration: t('cookie.dur.12m') },
      ],
    },
  ]), [t]);
}

function getStoredConsent() {
  try {
    const raw = localStorage.getItem('zertivo_cookie_consent');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function storeConsent(consent) {
  localStorage.setItem('zertivo_cookie_consent', JSON.stringify({ ...consent, timestamp: new Date().toISOString() }));
  // Notify listeners (e.g. PostHog loader in index.html) that they can now
  // initialise. Custom-event channel keeps the contract de-coupled from
  // any specific analytics vendor.
  try {
    window.dispatchEvent(new CustomEvent('zertivo:consent-changed', { detail: consent }));
    if (consent?.categories?.analytics) {
      window.dispatchEvent(new CustomEvent('zertivo:consent-granted', { detail: consent }));
    }
  } catch { /* IE / SSR fallthrough */ }
}

function CategoryToggle({ category, enabled, onChange, expanded, onToggle, t }) {
  return (
    <div className="rounded-2xl border border-[#E4E4E7] overflow-hidden transition-all duration-300">
      <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#F9FAFB] transition-colors" onClick={onToggle}>
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); if (!category.required) onChange(!enabled); }}
            className={`relative w-11 h-6 rounded-full transition-all duration-300 ${
              enabled ? 'bg-gradient-to-r from-[#0F8A5F] to-[#00D387]' : 'bg-[#F9FAFB]'
            } ${category.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            disabled={category.required}
            data-testid={`cookie-toggle-${category.id}`}
          >
            <motion.div
              className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md"
              animate={{ left: enabled ? '22px' : '2px' }}
              transition={{ duration: 0.2, ease }}
            />
          </button>
          <div>
            <span className="text-[14px] font-medium text-primary">{category.name}</span>
            {category.required && <span className="ml-2 text-[9px] tracking-wider uppercase font-bold text-[#71717A]">{t('cookie.required')}</span>}
          </div>
        </div>
        <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.3 }}>
          <ChevronDown className="w-4 h-4 text-[#71717A]" />
        </motion.div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease }}>
            <div className="px-4 pb-4 border-t border-[#F4F4F5]">
              <p className="text-[12px] text-[#71717A] leading-[1.7] mt-3 mb-3">{category.description}</p>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[#71717A]">
                    <th className="text-left py-1.5 font-bold uppercase tracking-wider">{t('cookie.col.cookie')}</th>
                    <th className="text-left py-1.5 font-bold uppercase tracking-wider">{t('cookie.col.purpose')}</th>
                    <th className="text-left py-1.5 font-bold uppercase tracking-wider">{t('cookie.col.duration')}</th>
                  </tr>
                </thead>
                <tbody>
                  {category.cookies.map((c, i) => (
                    <tr key={i} className="border-t border-[#F4F4F5]">
                      <td className="py-2 text-primary font-medium">{c.name}</td>
                      <td className="py-2 text-[#71717A]">{c.purpose}</td>
                      <td className="py-2 text-[#71717A]">{c.duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function CookieBanner() {
  const { t } = useLanguage();
  const categories = useCookieCategories();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState({ necessary: true, analytics: false, marketing: false, preferences: false });
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [consentId, setConsentId] = useState(null);

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored && stored.consent_id) {
      setConsentId(stored.consent_id);
      setConsent({ necessary: true, analytics: stored.analytics || false, marketing: stored.marketing || false, preferences: stored.preferences || false });
    } else {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveToBackend = useCallback(async (choices) => {
    try {
      const { data } = await api.post('/consent', { consent_id: consentId, ...choices });
      if (data.consent_id) {
        setConsentId(data.consent_id);
        storeConsent({ ...choices, consent_id: data.consent_id });
      }
    } catch {
      storeConsent({ ...choices, consent_id: consentId || 'local-' + Date.now() });
    }
  }, [consentId]);

  const handleAcceptAll = () => {
    const choices = { necessary: true, analytics: true, marketing: true, preferences: true };
    setConsent(choices);
    saveToBackend(choices);
    setVisible(false);
  };

  const handleRejectAll = () => {
    const choices = { necessary: true, analytics: false, marketing: false, preferences: false };
    setConsent(choices);
    saveToBackend(choices);
    setVisible(false);
  };

  const handleSaveCustom = () => {
    saveToBackend(consent);
    setVisible(false);
  };

  // Allow reopening via global function or event
  useEffect(() => {
    const open = () => { setShowDetails(true); setVisible(true); };
    window.openCookieSettings = open;
    window.addEventListener('cookie:open', open);
    return () => {
      delete window.openCookieSettings;
      window.removeEventListener('cookie:open', open);
    };
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 100 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 100 }}
          transition={{ duration: 0.6, ease }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-3xl mx-auto rounded-3xl bg-white/90 border border-[#E4E4E7] shadow-[0_-12px_40px_rgba(17,24,39,0.1),0_0_24px_rgba(17,24,39,0.04)] backdrop-blur-2xl overflow-hidden">
            <div className="h-[2px] bg-gradient-to-r from-transparent via-[#00D387] to-transparent" />

            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0F8A5F]/20 to-transparent flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#00D387]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-[16px] font-bold text-primary">{t('cookie.title')}</h3>
                    <p className="text-[11px] text-[#71717A]">{t('cookie.privacySubtitle')}</p>
                  </div>
                </div>
              </div>

              <p className="text-[13px] text-secondary leading-[1.7] mb-6">
                {t('cookie.description')}{' '}
                <Link to="/datenschutz" className="text-[#0F8A5F] hover:text-[#00D387] transition-colors animated-underline">
                  {t('cookie.learnMore')}
                </Link>
              </p>

              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease }}
                    className="mb-6"
                  >
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                      {categories.map(cat => (
                        <CategoryToggle
                          key={cat.id}
                          category={cat}
                          enabled={consent[cat.id]}
                          onChange={(v) => setConsent(prev => ({ ...prev, [cat.id]: v }))}
                          expanded={expandedCategory === cat.id}
                          onToggle={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                          t={t}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex flex-col sm:flex-row gap-3">
                {showDetails ? (
                  <>
                    <button onClick={handleSaveCustom} data-testid="cookie-save-btn"
                      className="btn-primary flex-1 py-3 font-medium text-[13px] tracking-wide flex items-center justify-center gap-2">
                      <Check className="w-4 h-4" /> {t('cookie.saveSelection')}
                    </button>
                    <button onClick={handleAcceptAll} data-testid="cookie-accept-all-detail-btn"
                      className="btn-outline flex-1 py-3 font-medium text-[13px] tracking-wide">
                      {t('cookie.acceptAll')}
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={handleAcceptAll} data-testid="cookie-accept-all-btn"
                      className="btn-primary flex-1 py-3 font-medium text-[13px] tracking-wide flex items-center justify-center gap-2">
                      <Check className="w-4 h-4" /> {t('cookie.acceptAll')}
                    </button>
                    <button onClick={handleRejectAll} data-testid="cookie-reject-all-btn"
                      className="btn-outline flex-1 py-3 font-medium text-[13px] tracking-wide">
                      {t('cookie.rejectAll')}
                    </button>
                    <button onClick={() => setShowDetails(true)} data-testid="cookie-settings-btn"
                      className="flex items-center justify-center gap-2 px-5 py-3 text-[13px] text-[#71717A] hover:text-primary transition-colors font-medium">
                      <Settings className="w-4 h-4" /> {t('cookie.settings')}
                    </button>
                  </>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-[#F4F4F5] flex flex-wrap items-center gap-4 text-[10px] text-[#A1A1AA]">
                <Link to="/datenschutz" className="hover:text-[#00D387] transition-colors">{t('footer.privacy')}</Link>
                <Link to="/impressum" className="hover:text-[#00D387] transition-colors">{t('footer.imprint')}</Link>
                <Link to="/agb" className="hover:text-[#00D387] transition-colors">{t('footer.terms')}</Link>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function CookieSettingsButton() {
  const { t } = useLanguage();
  return (
    <button
      onClick={() => window.openCookieSettings?.()}
      data-testid="reopen-cookie-settings-btn"
      className="text-[13px] text-[#71717A] hover:text-[#00D387] transition-colors flex items-center gap-1.5"
    >
      <Cookie className="w-3.5 h-3.5" /> {t('cookie.btnSettings')}
    </button>
  );
}
